import { Filter, Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import type {
  CategoryFilter as CategoryFilterType,
  ChurchFilter as ChurchFilterType,
  DonationFilter as DonationFilterType,
  ExpensesFilter as ExpensesFilterType,
  InventoryFilter as InventoryFilterType,
  MemberFilter as MemberFilterType,
  TransactionFilter as TransactionFilterType,
  UserFilter as UserFilterType,
} from '@/schemas/filterSchemas'
import IconInput from '@/components/custom/icon-input'
import { Button } from '@/components/ui/button'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer'
import { VisuallyHidden } from '@/components/ui/visually-hidden'

// Import forms and filters
import { ChurchForm } from '@/features/churches/components/ChurchForm'
import ChurchFilter from '@/features/churches/components/ChurchFilter'
import { DonationForm } from '@/features/donations/components/DonationForm'
import DonationFilter from '@/features/donations/components/DonationFilter'
import InventoryFilter from '@/features/inventory/components/InventoryFilter'
import { MemberForm } from '@/features/members/components/MemberForm'
import MemberFilter from '@/features/members/components/MemberFilter'
import { UserForm } from '@/features/users/components/UserForm'
import UserFilter from '@/features/users/components/UserFilter'
import { useIsMobile } from '@/hooks'
import SearchBarFilter from '@/components/custom/SearchBarFilter'
import { CategoryForm } from '@/features/inventory/components/CategoryForm'
import ExpensesFilter from '@/features/expenses/components/ExpensesFilter'
import { ExpenseCategoryForm } from '@/features/expenses/components/ExpenseCategoryForm'

type PageType =
  | 'churches'
  | 'members'
  | 'users'
  | 'donations'
  | 'registrations'
  | 'inventoryStock'
  | 'categories'
  | 'transactions'
  | 'expenses'
  | 'expenseCategories'

type FiltersType =
  | UserFilterType
  | MemberFilterType
  | ChurchFilterType
  | DonationFilterType
  | InventoryFilterType
  | CategoryFilterType
  | TransactionFilterType
  | ExpensesFilterType

interface ListPageHeaderProps {
  type: PageType
  searchValue: string
  onSearchChange: (value: string) => void
  onFilter: (filters: FiltersType) => void
  initialFilters: any
  // Form state management
  isFormOpen: boolean
  onFormOpen?: () => void
  onFormClose?: () => void
  // Optional overrides
  searchPlaceholder?: string
  showAddButton?: boolean
  className?: string
  customActions?: React.ReactNode
}

const pageConfigs = {
  churches: {
    title: 'Churches',
    searchPlaceholder: 'Church, Pastor, City',
  },
  donations: {
    title: 'Donations',
    searchPlaceholder: 'Donor, Receipt #',
  },
  registrations: {
    title: 'Registrations',
    searchPlaceholder: 'Donor, Receipt #',
  },
  inventoryStock: {
    title: 'Inventory',
    searchPlaceholder: 'Item, description',
  },
  members: {
    title: 'Members',
    searchPlaceholder: 'Name, email, phone',
  },
  users: {
    title: 'Users',
    searchPlaceholder: 'User, email, role',
  },
  categories: {
    title: 'Inventory Categories',
    searchPlaceholder: 'Search categories',
  },
  transactions: {
    title: 'Transactions',
    searchPlaceholder: 'Search transactions',
  },
  expenses: {
    title: 'Expenses',
    searchPlaceholder: 'Search expenses',
  },
  expenseCategories: {
    title: 'Expense Categories',
    searchPlaceholder: 'Search categories',
  },
}

export function ListPageHeader({
  type,
  searchValue,
  onSearchChange,
  onFilter,
  initialFilters,
  isFormOpen,
  onFormOpen,
  onFormClose,
  searchPlaceholder,
  showAddButton = true,
  className = 'space-y-3',
  customActions,
}: ListPageHeaderProps) {
  const isMobile = useIsMobile()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const config = pageConfigs[type]
  const placeholder = searchPlaceholder || config.searchPlaceholder

  const closeMobileDrawer = () => {
    if (!isMobile) return
    setIsDrawerOpen(false)
  }

  const renderFilter = () => {
    const filterProps = {
      onFilter,
      initialFilters,
      onCloseDrawer: closeMobileDrawer,
    }
    const filters: Record<PageType, React.ReactElement> = {
      churches: <ChurchFilter {...filterProps} />,
      donations: <DonationFilter {...filterProps} />,
      registrations: <DonationFilter {...filterProps} />,
      inventoryStock: <InventoryFilter {...filterProps} />,
      members: <MemberFilter {...filterProps} />,
      users: <UserFilter {...filterProps} />,
      categories: <SearchBarFilter type="categories" {...filterProps} />,
      transactions: <SearchBarFilter type="transactions" {...filterProps} />,
      expenses: <ExpensesFilter {...filterProps} />,
      expenseCategories: <SearchBarFilter type="categories" {...filterProps} />,
    }
    return filters[type]
  }

  const renderSingleForm = () => {
    const formProps = {
      type: 'add' as const,
      onClose: onFormClose,
      open: isFormOpen,
    }

    const forms: Partial<Record<PageType, React.ReactElement>> = {
      churches: <ChurchForm {...formProps} />,
      donations: <DonationForm {...formProps} />,
      registrations: <DonationForm {...formProps} />,
      members: <MemberForm {...formProps} />,
      users: <UserForm {...formProps} />,
      categories: <CategoryForm {...formProps} />,
      expenseCategories: <ExpenseCategoryForm {...formProps} />,
    }
    return forms[type]
  }

  const isFiltering = useMemo(() => {
    if (!initialFilters) return false

    return Object.entries(initialFilters).some(([key, value]) => {
      // Check search
      if (key === 'search' && typeof value === 'string')
        return value && value.trim() !== ''

      // Check arrays
      if (Array.isArray(value)) return value.length > 0

      // Check booleans
      if (typeof value === 'boolean') return value === true

      // Check strings
      if (typeof value === 'string') return value.trim() !== ''

      return false
    })
  }, [initialFilters])

  return (
    <div className={className}>
      {/* Mobile Layout */}
      <div className="block lg:hidden space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold">{config.title}</h1>
          <div className="flex gap-2">
            {/* Filter Drawer */}
            <Drawer
              open={isDrawerOpen}
              onOpenChange={setIsDrawerOpen}
              direction="right"
            >
              <DrawerTrigger asChild>
                <Button
                  variant="outline"
                  className="h-10 lg:h-9"
                  size="default"
                >
                  <Filter
                    className={`h-4 w-4 mr-2 ${isFiltering ? 'text-red-500' : ''}`}
                  />
                  Filter
                </Button>
              </DrawerTrigger>
              <DrawerContent className="min-w-[95%] max-w-[95%] rounded-md">
                <DrawerHeader>
                  <DrawerTitle>Filter {config.title}</DrawerTitle>
                  <VisuallyHidden>
                    <DrawerDescription>
                      Filter options for {config.title.toLowerCase()}
                    </DrawerDescription>
                  </VisuallyHidden>
                </DrawerHeader>
                <div className="px-4 pb-16 space-y-4">
                  <div className="w-full [&_button]:w-full">
                    {renderFilter()}
                  </div>
                </div>
              </DrawerContent>
            </Drawer>

            {/* Add Button */}
            {showAddButton && (
              <Button
                onClick={onFormOpen}
                className="w-auto h-10"
                size="default"
              >
                <Plus className="h-4 w-4 mr-2" /> Add
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden lg:block">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="text-xl font-semibold">{config.title}</h1>
          <div className="flex gap-3">
            <IconInput
              placeholder={placeholder}
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              className="min-w-64"
            />
            {![
              'items',
              'categories',
              'transactions',
              'expenseCategories',
            ].includes(type) && renderFilter()}
            {showAddButton && (
              <Button
                onClick={onFormOpen}
                className="w-auto h-9"
                size="default"
              >
                <Plus className="h-4 w-4 mr-2" /> Add
              </Button>
            )}
            {customActions}
          </div>
        </div>
      </div>

      {/* Single Form Instance - rendered outside responsive layouts */}
      {showAddButton && renderSingleForm()}
    </div>
  )
}
